import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/shadcn/sheet'
import { RestSchemas } from '@/contexts/QueryProvider'
import { getTicketTypeLabel } from '@/pages/Subscriptions/Requests/types'
import { buildTicketSummaryLines, formatCreatedBy, statusLabel } from './requestDetailUtils'
import { EMPTY_TICKET_LOOKUPS, formatMappedName, TicketLookupMaps } from './ticketLookups'

type ExistingRequest = RestSchemas['Messages.Requests.ExistingRequest'] & { subject?: string | null }

type PlatformTicketRequestDetailSheetProps = {
  request: ExistingRequest
  open: boolean
  onOpenChange: (open: boolean) => void
  lookups?: TicketLookupMaps
}

export function PlatformTicketRequestDetailSheet({
  request,
  open,
  onOpenChange,
  lookups = EMPTY_TICKET_LOOKUPS,
}: PlatformTicketRequestDetailSheetProps) {
  const summaryLines = buildTicketSummaryLines(request.ticketType, request.ticket, request.subject, lookups)

  let formattedTicket = request.ticket ?? '{}'
  try {
    formattedTicket = JSON.stringify(JSON.parse(request.ticket ?? '{}'), null, 2)
  } catch {
    // keep raw ticket string
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        className="w-full overflow-y-auto sm:max-w-xl"
        onCloseAutoFocus={(event) => event.preventDefault()}
      >
        <SheetHeader>
          <SheetTitle>Request #{request.id}</SheetTitle>
          <SheetDescription>
            {getTicketTypeLabel(request.ticketType)} — {statusLabel(request.status)}
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6 space-y-4 text-sm">
          <div>
            <span className="font-medium">Organisation:</span>{' '}
            {request.organisationId != null
              ? formatMappedName(request.organisationId, lookups.organisationNames, 'Organisation')
              : '—'}
          </div>
          <div>
            <span className="font-medium">Created:</span>{' '}
            {request.createdAt ? new Date(request.createdAt).toLocaleString() : '—'}
          </div>
          <div>
            <span className="font-medium">Created by:</span> {formatCreatedBy(request.createdBy, lookups)}
          </div>
          {request.closedAt && (
            <div>
              <span className="font-medium">Closed:</span> {new Date(request.closedAt).toLocaleString()}
              {request.closedBy != null && <> by {formatCreatedBy(request.closedBy, lookups)}</>}
            </div>
          )}
          <div>
            <span className="mb-2 block font-medium">Summary</span>
            <div className="space-y-1 rounded-md bg-muted p-3 text-xs">
              {summaryLines.map((line, index) => (
                <div key={`${index}-${line}`}>{line}</div>
              ))}
            </div>
          </div>
          <div>
            <span className="mb-2 block font-medium">Raw payload</span>
            <pre className="max-h-[40vh] overflow-auto rounded-md bg-muted p-3 text-xs">{formattedTicket}</pre>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  )
}
